import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppLink, Button, ErrorText, Flex, Page, PageHeader, SearchField, Table, Text } from '@cmaas/ui';
import type { AdminProperty } from '../../api/types';
import { apiFetch, CmaasApiError } from '../../api/client';
import { usePage } from '../../api/usePage';
import { useSearchQuery } from '../../hooks/useSearchQuery';
import { NewPropertyModal } from './NewPropertyModal';

const mutationError = (err: unknown) => (err instanceof CmaasApiError ? err.message : 'Something went wrong.');

/** Cross-tenant list/create (P5c gap #1) — tenant is a filter/column here, never a route-level scope. */
export function PropertiesPage() {
  const queryClient = useQueryClient();
  const { input, setInput, q } = useSearchQuery();
  const key = ['properties', q ?? ''];
  const page = usePage<AdminProperty>(key, '/v1/admin/properties', { q });
  const [modalOpen, setModalOpen] = useState(false);

  const toggleStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'disabled' }) =>
      apiFetch(`/v1/admin/properties/${id}/actions/${status === 'active' ? 'disable' : 'enable'}`, {
        method: 'POST',
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  const properties = page.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <Page>
      <PageHeader
        title="Properties"
        actions={
          <Flex gap="3" align="center">
            <SearchField value={input} onChange={setInput} placeholder="Search by name…" />
            <Button onClick={() => setModalOpen(true)}>+ Add property</Button>
          </Flex>
        }
      />

      <NewPropertyModal open={modalOpen} onOpenChange={setModalOpen} />

      {page.isLoading ? (
        <Text as="p">Loading…</Text>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Tenant</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Platform</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {properties.map((p) => (
              <Table.Row key={p.id}>
                <Table.Cell>
                  <AppLink to={`/properties/${p.id}`}>{p.name}</AppLink>
                </Table.Cell>
                <Table.Cell>
                  <AppLink to={`/tenants/${p.tenant_id}`}>{p.tenant_id}</AppLink>
                </Table.Cell>
                <Table.Cell>{p.apple_platform}</Table.Cell>
                <Table.Cell>{p.status}</Table.Cell>
                <Table.Cell>
                  <Button
                    type="button"
                    variant="soft"
                    size="1"
                    onClick={() => toggleStatus.mutate({ id: p.id, status: p.status })}
                    disabled={toggleStatus.isPending}
                  >
                    {p.status === 'active' ? 'Disable' : 'Enable'}
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      {page.hasNextPage && (
        <Button
          type="button"
          variant="soft"
          onClick={() => void page.fetchNextPage()}
          disabled={page.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
      {toggleStatus.isError && <ErrorText>{mutationError(toggleStatus.error)}</ErrorText>}
    </Page>
  );
}
